<template>
  <v-container class="content">
    <!--loading screen when add video request is being handled-->
    <LoadingScreen :loading="loading" />

    <!-- Popup Nav Header -->
    <div class="nav" @click="goBack">
      <v-icon size="12px" color="#00457C">arrow_back_ios</v-icon>Back
    </div>
    <div class="header text-left">Create Popup</div>

    <!-- Image Upload -->
    <v-row class="photo-label">
      <v-col cols="3">
        <label for="cover-photo">Cover Photo</label>
      </v-col>
    </v-row>
    <v-row class="photo-picker">
      <v-col cols="6" md="3">
        <input disabled id="cover-photo" type="text" v-model="imageFileName" />
        <br />
        <input
          style="display: none"
          ref="imageFileInput"
          type="file"
          accept="*/*;q=0.8"
          @change="(e) => setImage(e.target.files[0])"
        />
      </v-col>
      <v-col cols="2">
        <label v-on:click="clickImageInput" id="browse-btn">Browse</label>
      </v-col>
    </v-row>

    <!-- Start and End Dates -->
    <v-row class="date-labels">
      <v-col cols="6" md="3">
        <label for="startDate">Start Date</label>
      </v-col>
      <v-col cols="6" md="3">
        <input
          type="datetime-local"
          id="startDate"
          v-model="startDate"
          @input="$v.startDate.$touch()"
        />
        <div
          class="error-msg"
          v-show="$v.startDate.$invalid && $v.startDate.$dirty"
        >
          Start date has to be at least one day from today.
        </div>
      </v-col>
    </v-row>
    <v-row class="date-pickers">
      <v-col cols="6" md="3">
        <label for="endDate">End Date</label>
      </v-col>
      <v-col cols="6" md="3">
        <input
          type="datetime-local"
          id="endDate"
          v-model="endDate"
          @input="$v.endDate.$touch()"
        />
        <div
          class="error-msg"
          v-show="$v.endDate.$invalid && $v.endDate.$dirty"
        >
          End date has to be at least one day from start date.
        </div>
      </v-col>
    </v-row>

    <!-- Schedule -->
    <v-row>
      <v-col cols="6" md="3">
        <label for="popup-schedule">Schedule</label>
        <select
          id="popup-schedule"
          v-model="schedule"
          @input="$v.schedule.$touch()"
        >
          <option disabled value>Select one only</option>
          <option>Daily</option>
          <option>Weekly</option>
          <option>Monthly</option>
          <option>Yearly</option>
        </select>
        <div
          class="error-msg"
          v-show="$v.schedule.$invalid && $v.schedule.$dirty"
        >
          This is a required field.
        </div>
      </v-col>
    </v-row>

    <!-- Topics -->
    <v-row>
      <v-col cols="6" md="3">
        <label for="popup-topic">Topic (Select 1 or more)</label>
        <select
          id="popup-topic"
          v-model="topics"
          multiple
          @input="$v.topics.$touch()"
        >
          <option v-for="topic in allTopics" :key="topic._id">
            {{ topic.topic }}
          </option>
        </select>
        <div class="error-msg" v-show="$v.topics.$invalid && $v.topics.$dirty">
          This is a required field.
        </div>
      </v-col>
    </v-row>

    <!-- Title -->
    <v-row>
      <v-col cols="12">
        <label for="title">Title</label>
        <input
          id="title"
          type="text"
          v-model="title"
          @input="$v.title.$touch()"
        />
        <div class="error-msg" v-show="$v.title.$invalid && $v.title.$dirty">
          This is a required field.
        </div>
        <br />
      </v-col>
    </v-row>

    <!-- Body -->
    <v-row>
      <v-col cols="12">
        <label for="popup-body">Body</label>
        <textarea
          id="popup-body"
          v-model="body"
          @input="$v.body.$touch()"
        ></textarea>
        <div class="error-msg" v-show="$v.body.$invalid && $v.body.$dirty">
          This is a required field.
        </div>
      </v-col>
    </v-row>

    <!-- Button Configurations -->
    <v-row>
      <v-col cols="6" md="4">
        <label for="popup-btn-title">Button Title</label>
        <input
          id="popup-btn-title"
          type="text"
          v-model="btnTitle"
          @input="$v.btnTitle.$touch()"
        />
        <div
          class="error-msg"
          v-show="$v.btnTitle.$invalid && $v.btnTitle.$dirty"
        >
          This is a required field.
        </div>
        <br />
      </v-col>
      <v-col cols="6" md="4">
        <label for="popup-btn-url">Button URL</label>
        <input
          id="popup-btn-url"
          type="text"
          v-model="redirectUrl"
          @input="$v.redirectUrl.$touch()"
        />
        <div
          class="error-msg"
          v-show="$v.redirectUrl.$invalid && $v.redirectUrl.$dirty"
        >
          Please enter a URL in the following format: http://www.example.com/.
        </div>
        <br />
      </v-col>
    </v-row>

    <!-- Form Submit Button -->
    <v-row>
      <v-col cols="6" md="3">
        <v-btn color="#00457C" :disabled="$v.$invalid" @click="submitPopupForm"
          >Submit</v-btn
        >
      </v-col>

      <v-col cols="4" md="3">
        <v-btn color="#00457C" :disabled="$v.$invalid" @click="preview"
          >Preview</v-btn
        >
      </v-col>
    </v-row>

    <v-row v-if="success">
      <v-col cols="12">
        <v-alert type="success">Popup added successfully.</v-alert>
      </v-col>
    </v-row>

    <v-row v-if="error">
      <v-col cols="12">
        <v-alert type="error">Error occurred. Please try again.</v-alert>
      </v-col>
    </v-row>

    <div
      v-if="currentPopupView !== null"
      class="dialog-popup-wrapper dialog-screen-wrapper"
    >
      <div
        class="content"
        :style="{ backgroundImage: 'url(' + currentPopupView.imgUrl + ')' }"
      >
        <div class="close-section">
          <v-icon size="20px" color="primary" v-on:click="closePopupDialog"
            >mdi-close</v-icon
          >
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import Axios from "axios";
import LoadingScreen from "./commons/LoadingScreen";
import { required, url } from "vuelidate/lib/validators";

//form validation: check if end date falls after start date
let checkEndDate = (value, vm) => {
  let startDate = new Date(vm.startDate).getTime();
  let input = new Date(value).getTime();

  if (input > startDate) {
    return true;
  } else {
    return false;
  }
};

export default {
  components: {
    LoadingScreen,
  },

  mounted() {
    // Fetch all topics
    Axios.get(process.env.VUE_APP_HOST_SERVER + "/api/topics/get_all")
      .then(({ data }) => {
        console.log("Successfully retrieved all details");
        this.allTopics = data;
      })
      .catch((err) => console.log(err));
  },

  data() {
    return {
      title: "",
      body: "",
      topics: [],
      startDate: "",
      endDate: "",
      schedule: "",
      btnTitle: "",
      redirectUrl: "",
      imageFile: null,
      imageFileName: "",
      loading: false,
      success: false,
      error: false,
      allTopics: [],
      currentPopupView: null,
    };
  },

  validations: {
    title: {
      required,
    },

    body: {
      required,
    },

    startDate: {
      required,
    },

    schedule: {
      required,
    },

    topics: {
      required,
    },

    endDate: {
      required,
      checkEndDate,
    },

    btnTitle: {
      required,
    },

    redirectUrl: {
      required,
      url,
    },
  },

  methods: {
    closePopupDialog() {
      this.currentPopupView = null;
    },
    preview() {
      this.currentPopupView = {
        imgUrl: URL.createObjectURL(this.imageFile),
      };

      // console.log(this.currentAccouncementView.imgUrl);
    },
    goBack() {
      this.$store.dispatch("loadView", "Popup");
    },
    // TODO: validate redirect url to have http://
    // TODO: validate image to be of a certain aspect ratio
    setImage(file) {
      if (file !== null) {
        // Set image file
        this.imageFile = file;
        this.imageFileName = file.name;
      }
    },
    submitPopupForm() {
      this.loading = true; //enable loading screen once user clicks submit
      const popupFormData = new FormData();
      popupFormData.append("imageFile", this.imageFile);
      // Upload image to AWS
      Axios.post(
        process.env.VUE_APP_HOST_SERVER + "/api/image_upload/popups",
        popupFormData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
        .then((res) => {
          console.log("Successfully uploaded popup image ", res.data.imageUrl);
          const imageUrl = res.data.imageUrl;
          const startDateTime = this.startDate;
          const endDateTime = this.endDate;

          const popupDetails = {
            imgUrl: imageUrl,
            title: this.title,
            body: this.body,
            redirectUrl: this.redirectUrl,
            topics: this.topics,
            startDate: new Date(startDateTime),
            endDate: new Date(endDateTime),
            schedule: this.schedule,
            btnTitle: this.btnTitle,
          };
          // Add popup to MongoDB
          Axios.post(
            process.env.VUE_APP_HOST_SERVER + "/api/popups/add",
            popupDetails
          ).then(() => {
            console.log("Successfully created popup ", popupDetails);
            this.loading = false; //disable loading screen once request is handled
            this.success = true; //show success message
            this.error = false;
          });
        })
        .catch((err) => {
          console.log(err);
          this.error = true;
        });
    },
    clickImageInput() {
      this.$refs.imageFileInput.click();
    },
  },
};
</script>

<style scoped>
.dialog-popup-wrapper .content {
  background-size: cover;
  border-radius: 20px;
  width: 300px;
  height: 400px;
  padding: 20px;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.content {
  box-shadow: 0px 1px 20px rgba(163, 163, 163, 0.25);
  border: 1px solid #eeeeee;
  padding: 25px;
  text-align: initial;
}

.content .row {
  padding-bottom: 18px;
}

.content .row:nth-last-child(1) {
  padding-bottom: 0;
}

.content .nav {
  color: #00457c;
  font-weight: bold;
  margin-bottom: 40px;
  font-size: 16px;
  cursor: pointer;
}

.content .header {
  font-size: 26px;
  margin-bottom: 20px;
  font-weight: normal;
}

.content button {
  width: 100%;
  font-size: 14px;
  font-weight: normal;
  color: white;
  text-transform: none;
  letter-spacing: normal;
}

label {
  font-size: 14px;
  color: #6f6f6f;
  margin-bottom: 10px;
  display: block;
}

.content .date-labels,
.content .photo-label {
  padding-bottom: 0;
}

.content .date-labels > div,
.content .photo-label > div {
  padding-bottom: 0;
}

.content .date-pickers > div,
.content .photo-picker > div {
  padding-top: 0;
}

label#browse-btn {
  display: inline-block;
  background: #ffffff;
  box-shadow: 0px 1px 5px rgba(120, 120, 120, 0.25);
  border-radius: 5px;
  color: #9b9b9b;
  padding: 10px;
  margin-bottom: 0;
}

input[type="text"],
input[type="number"],
input[type="date"],
textarea,
select {
  padding: 10px;
  box-sizing: border-box;
  border: 1px solid #dfdfdf;
  border-radius: 5px;
  width: 100%;
  font-size: 14px;
}

.error-msg {
  font-size: 10px;
  color: #ea6254;
  margin-top: 5px;
}
</style>
